import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Users, Briefcase, Wallet, TrendingUp, DollarSign, 
  FileText, ArrowLeft, CheckCircle, XCircle, Trash2, Clock, Eye, Image, FileCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOCUMENT_TYPE_LABELS = {
  cedula: 'Cédula de Ciudadanía',
  tarjeta_profesional: 'Tarjeta Profesional',
  educacion_superior: 'Certificado de Educación Superior',
  rut: 'RUT',
  certificado_laboral: 'Certificado Laboral',
  fotos_trabajos: 'Fotos de Trabajos',
  rut_conjunto: 'RUT del Conjunto/Hotel'
};

export default function AdminPanel({ user, token }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docStats, setDocStats] = useState({});
  const [docFilter, setDocFilter] = useState('pending');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showDocPreview, setShowDocPreview] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user.role !== 'admin') {
      toast.error('Acceso denegado');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [statsRes, usersRes, jobsRes, workersRes, transactionsRes, commissionsRes, docsRes, docStatsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, config),
        axios.get(`${API}/admin/users`, config),
        axios.get(`${API}/admin/jobs`, config),
        axios.get(`${API}/admin/workers`, config),
        axios.get(`${API}/admin/transactions`, config),
        axios.get(`${API}/admin/commissions`, config),
        axios.get(`${API}/admin/documents?status=${docFilter}`, config),
        axios.get(`${API}/admin/documents/stats`, config)
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setJobs(jobsRes.data);
      setWorkers(workersRes.data);
      setTransactions(transactionsRes.data);
      setCommissions(commissionsRes.data);
      setDocuments(docsRes.data);
      setDocStats(docStatsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/admin/documents?status=${docFilter}`, config);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [docFilter]);

  const handleVerifyDocument = async (docId, verified) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/admin/documents/${docId}/verify?verified=${verified}`, {}, config);
      toast.success(verified ? 'Documento verificado' : 'Documento rechazado');
      fetchDocuments();
      // Update stats
      const statsRes = await axios.get(`${API}/admin/documents/stats`, config);
      setDocStats(statsRes.data);
    } catch (error) {
      toast.error('Error al actualizar documento');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/admin/documents/${docId}`, config);
      toast.success('Documento eliminado');
      fetchDocuments();
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('¿Estás seguro de eliminar este trabajo?')) return;
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/admin/jobs/${jobId}`, config);
      toast.success('Trabajo eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar trabajo');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status]}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Cargando panel de administración...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#3A4F6C] to-[#2A3B4F] text-white p-6 sticky top-0 z-40">
        <div className="container mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate('/dashboard')}
            className="text-white hover:text-[#FF8935] mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="h-6 w-6" />
            Volver
          </button>
          <h1 className="text-3xl font-bold" data-testid="admin-title">
            PANEL DE ADMINISTRACIÓN
          </h1>
          <p className="text-slate-300 mt-1">Constru/clic Management Dashboard</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <Card data-testid="stat-users">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Usuarios</p>
                  <p className="text-3xl font-bold text-[#3A4F6C]">{stats.total_users}</p>
                </div>
                <Users className="h-12 w-12 text-[#FF8935]" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-workers">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Trabajadores</p>
                  <p className="text-3xl font-bold text-[#3A4F6C]">{stats.total_workers}</p>
                </div>
                <Briefcase className="h-12 w-12 text-[#FF8935]" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-jobs">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Trabajos</p>
                  <p className="text-3xl font-bold text-[#3A4F6C]">{stats.total_jobs}</p>
                  <p className="text-xs text-slate-400">
                    {stats.completed_jobs} completados
                  </p>
                </div>
                <FileText className="h-12 w-12 text-[#FF8935]" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-revenue">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Ingresos Comisión</p>
                  <p className="text-3xl font-bold text-green-600">
                    ${stats.total_revenue?.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-400">
                    De ${stats.total_transactions?.toLocaleString()} total
                  </p>
                </div>
                <DollarSign className="h-12 w-12 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="jobs" className="w-full">
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="jobs">Trabajos</TabsTrigger>
            <TabsTrigger value="workers">Trabajadores</TabsTrigger>
            <TabsTrigger value="documents" className="relative">
              Documentos
              {docStats.pending > 0 && (
                <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs h-5 w-5 p-0 flex items-center justify-center rounded-full">
                  {docStats.pending}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="users">Usuarios</TabsTrigger>
            <TabsTrigger value="commissions">Comisiones</TabsTrigger>
            <TabsTrigger value="transactions">Transacciones</TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <Card>
              <CardHeader>
                <CardTitle>Todos los Trabajos ({jobs.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {jobs.map(job => (
                    <div
                      key={job.id}
                      className="p-4 border border-slate-200 rounded-lg flex justify-between items-start"
                    >
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-2">
                          <div className="flex-1">
                            <h3 className="font-bold">{job.title}</h3>
                            <p className="text-sm text-slate-600">{job.description}</p>
                          </div>
                          {getStatusBadge(job.status)}
                        </div>
                        <div className="flex gap-4 text-xs text-slate-500">
                          <span>ID: {job.id.substring(0, 8)}</span>
                          <span>Categoría: {job.category}</span>
                          {job.budget && <span className="text-[#FF8935] font-semibold">${job.budget.toLocaleString()}</span>}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteJob(job.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Workers Tab */}
          <TabsContent value="workers">
            <Card>
              <CardHeader>
                <CardTitle>Trabajadores Registrados ({workers.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {workers.map(worker => (
                    <div
                      key={worker.id}
                      className="p-4 border border-slate-200 rounded-lg"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-bold">ID: {worker.user_id?.substring(0, 8)}</h3>
                          <p className="text-sm text-slate-600">{worker.bio}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-[#FF8935]">
                            ${worker.hourly_rate?.toLocaleString()}/hora
                          </p>
                          <p className="text-sm text-slate-500">
                            ⭐ {worker.rating.toFixed(1)} ({worker.total_jobs} trabajos)
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {worker.specialties?.map((s, i) => (
                          <Badge key={i} className="bg-[#FF8935]/10 text-[#FF8935]">
                            {s}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Verification Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <FileCheck className="h-5 w-5 text-blue-500" />
                    Verificación de Documentos
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant={docFilter === 'pending' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDocFilter('pending')}
                      className={docFilter === 'pending' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
                    >
                      <Clock className="h-4 w-4 mr-1" />
                      Pendientes ({docStats.pending || 0})
                    </Button>
                    <Button
                      variant={docFilter === 'verified' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDocFilter('verified')}
                      className={docFilter === 'verified' ? 'bg-green-500 hover:bg-green-600' : ''}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Verificados ({docStats.verified || 0})
                    </Button>
                    <Button
                      variant={docFilter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDocFilter('all')}
                    >
                      Todos ({docStats.total || 0})
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay documentos {docFilter === 'pending' ? 'pendientes' : docFilter === 'verified' ? 'verificados' : ''}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map(doc => (
                      <div
                        key={doc.id}
                        className="p-4 border border-slate-200 rounded-lg"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge className="bg-blue-100 text-blue-800">
                                {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
                              </Badge>
                              {doc.user_type === 'property_admin' ? (
                                <Badge className="bg-purple-100 text-purple-800">
                                  Administrador de Conjunto
                                </Badge>
                              ) : (
                                <Badge className="bg-orange-100 text-orange-800">
                                  Trabajador
                                </Badge>
                              )}
                              {doc.verified ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Verificado
                                </Badge>
                              ) : (
                                <Badge className="bg-yellow-100 text-yellow-700">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Pendiente
                                </Badge>
                              )}
                            </div>
                            <p className="font-semibold text-slate-800">{doc.worker_name || 'Usuario'}</p>
                            <p className="text-sm text-slate-500">{doc.worker_email}</p>
                            {doc.building_name && (
                              <p className="text-xs text-purple-600 mt-1">
                                🏢 {doc.building_name}{doc.building_address ? ` — ${doc.building_address}` : ''}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                              <span>Archivo: {doc.file_name}</span>
                              {doc.description && <span>• {doc.description}</span>}
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                              Subido: {new Date(doc.uploaded_at).toLocaleDateString('es-CO')}
                            </p>
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedDoc(doc);
                                setShowDocPreview(true);
                              }}
                              className="text-blue-600"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Ver
                            </Button>
                            
                            {!doc.verified && (
                              <Button
                                size="sm"
                                onClick={() => handleVerifyDocument(doc.id, true)}
                                className="bg-green-500 hover:bg-green-600 text-white"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Verificar
                              </Button>
                            )}
                            
                            {doc.verified && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleVerifyDocument(doc.id, false)}
                                className="text-yellow-600 border-yellow-300"
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Revocar
                              </Button>
                            )}
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDocument(doc.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Usuarios Registrados ({users.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map(u => (
                    <div
                      key={u.id}
                      className="p-4 border border-slate-200 rounded-lg flex justify-between items-center"
                    >
                      <div>
                        <h3 className="font-bold">{u.full_name}</h3>
                        <p className="text-sm text-slate-600">{u.email}</p>
                        <p className="text-xs text-slate-500">ID: {u.id.substring(0, 8)}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={
                          u.role === 'admin' ? 'bg-red-100 text-red-800' :
                          u.role === 'worker' ? 'bg-blue-100 text-blue-800' :
                          u.role === 'both' ? 'bg-purple-100 text-purple-800' :
                          'bg-slate-100 text-slate-800'
                        }>
                          {u.role}
                        </Badge>
                        {u.phone && <p className="text-xs text-slate-500 mt-1">{u.phone}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions">
            <Card>
              <CardHeader>
                <CardTitle>
                  Comisiones (10%) - Total: ${commissions.reduce((sum, c) => sum + c.commission, 0).toLocaleString()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {commissions.map((comm, idx) => (
                    <div
                      key={idx}
                      className="p-4 border border-slate-200 rounded-lg"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold">{comm.job_title}</h3>
                          <p className="text-xs text-slate-500">Job ID: {comm.job_id?.substring(0, 8)}</p>
                          <p className="text-sm text-slate-600 mt-1">
                            Cliente: {comm.client_id?.substring(0, 8)} → 
                            Trabajador: {comm.worker_id?.substring(0, 8)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">
                            +${comm.commission?.toLocaleString()}
                          </p>
                          <p className="text-sm text-slate-500">
                            De ${comm.final_price?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Transacciones Recientes ({transactions.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {transactions.slice(0, 50).map(t => (
                    <div
                      key={t.id}
                      className="p-4 border border-slate-200 rounded-lg flex justify-between items-center"
                    >
                      <div>
                        <p className="font-semibold">{t.description}</p>
                        <p className="text-xs text-slate-500">
                          Usuario: {t.user_id?.substring(0, 8)} | {t.type}
                        </p>
                        <p className="text-xs text-slate-400">
                          {new Date(t.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${
                          t.amount > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {t.amount > 0 ? '+' : ''}${Math.abs(t.amount).toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500">
                          Saldo: ${t.balance_after?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Document Preview Dialog */}
        <Dialog open={showDocPreview} onOpenChange={setShowDocPreview}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>
                {selectedDoc && (DOCUMENT_TYPE_LABELS[selectedDoc.document_type] || selectedDoc.document_type)}
              </DialogTitle>
              <DialogDescription>
                {selectedDoc?.worker_name} - {selectedDoc?.file_name}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              {selectedDoc && (
                selectedDoc.file_url.endsWith('.pdf') ? (
                  <iframe
                    src={`${BACKEND_URL}${selectedDoc.file_url}?auth=${token}`}
                    className="w-full h-[60vh] border rounded-lg"
                    title="Document Preview"
                  />
                ) : (
                  <img
                    src={`${BACKEND_URL}${selectedDoc.file_url}?auth=${token}`}
                    alt="Document"
                    className="w-full max-h-[60vh] object-contain border rounded-lg"
                  />
                )
              )}
            </div>
            <div className="flex justify-between mt-4">
              <Button
                variant="outline"
                onClick={() => window.open(`${BACKEND_URL}${selectedDoc?.file_url}?auth=${token}`, '_blank')}
              >
                Abrir en nueva pestaña
              </Button>
              <div className="flex gap-2">
                {selectedDoc && !selectedDoc.verified && (
                  <Button
                    onClick={() => {
                      handleVerifyDocument(selectedDoc.id, true);
                      setShowDocPreview(false);
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Verificar Documento
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowDocPreview(false)}>
                  Cerrar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
